import { ListContainer, SearchBar, Stack, pn } from "@compass/core";
import { ListFooter } from "components";
import {
  StudentViewResource,
  SurveyResponseListDto,
  SurveyResponseListFilter,
  SurveyResponseListResource,
  StudentApplicationFilter as StudentApplicationFilterModel,
  Permission,
  StudentApplicationListFilter,
  StudentApplicationListDto,
  StudentApplicationListResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useInitListFilter, useList, useListFilter } from "hooks";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { SurveyResponseTable } from "pages/cobro/survey-responses/SurveyResponseListPage/SurveyResponseTable";
import { SurveyResponseFilter } from "pages/cobro/survey-responses/SurveyResponseFilter";
import { StudentApplicationTable } from "pages/students/student-applications/StudentApplicationListPage/StudentApplicationTable";
import { StudentApplicationFilter } from "pages/students/student-applications/StudentApplicationListPage/StudentApplicationFilter";

export const StudentApplications = (props: {
  initialLoad: boolean;
  studentId: number;
  activeSchoolYear: number | undefined;
  onGetList: () => void;
  viewResource: StudentViewResource;
}) => {
  const ROOT_PERMISSION: Permission = Permission.StudentApplication;
  
  const { listFilter, setListFilter } = useInitListFilter<StudentApplicationListFilter>({
    sortObjects: [{ property: pn<StudentApplicationListDto>("submittedDate"), isDesc: true }],
    groupFilter: {
    },
  });

  const [initListFilter] = useState<StudentApplicationListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentApplicationListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentApplicationListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentApplications.getStudentList(listFilter, props.studentId).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentApplications.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentApplications.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentApplicationListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StudentApplicationFilter
        filter={listFilter.groupFilter || ({} as StudentApplicationFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.groupFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.studentApplications.getIds(listFilter);

  return (
    <Stack spacing="md" height="100%" overflow="auto">
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentApplicationTable
              openNewTab={true}
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentApplications={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
    </Stack>
  );
};
